import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { supabase } from '@/integrations/supabase/client';
import { MapPin, MessageCircle, Calendar } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

interface TravelingMember {
  user_id: string;
  current_location: string;
  location_message: string | null;
  available_until: string;
  member: {
    id: string;
    name: string;
    image: string | null;
    company: string;
    role: string;
    country: string;
  };
}

export const TravelingMembersWidget: React.FC = () => {
  const [members, setMembers] = useState<TravelingMember[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  const fetchTravelingMembers = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Récupérer le pays de l'utilisateur
      const { data: currentMember } = await supabase
        .from('club_members')
        .select('country')
        .eq('user_id', user.id)
        .single();

      if (!currentMember) return;

      // Récupérer les membres en déplacement dans le même pays
      const { data, error } = await supabase
        .from('user_presence')
        .select(`
          user_id,
          current_location,
          location_message,
          available_until,
          club_members!inner (
            id,
            name,
            image,
            company,
            role,
            country
          )
        `)
        .eq('status', 'traveling')
        .eq('club_members.country', currentMember.country)
        .neq('user_id', user.id)
        .gt('available_until', new Date().toISOString())
        .order('available_until', { ascending: true });

      if (error) throw error;

      const formatted = data?.map(item => ({
        user_id: item.user_id,
        current_location: item.current_location || '',
        location_message: item.location_message,
        available_until: item.available_until || '',
        member: Array.isArray(item.club_members) 
          ? item.club_members[0] 
          : item.club_members
      })).filter(item => item.member) || [];

      setMembers(formatted);
    } catch (error) {
      console.error('Erreur chargement membres en déplacement:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTravelingMembers();

    // 🔥 PHASE 1: Polling 2 min au lieu de Realtime
    import('@/utils/realtimeOptimizer').then(({ createPollingInterval }) => {
      const stopPolling = createPollingInterval(
        'user_presence',
        fetchTravelingMembers,
        true
      );

      return () => {
        stopPolling();
      };
    });
  }, []);

  const handleContact = (memberId: string) => {
    navigate(`/networking?contact=${memberId}`);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-orange-600" />
            Membres en déplacement
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Chargement...</p>
        </CardContent>
      </Card>
    );
  }

  if (members.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-orange-600" />
            Membres en déplacement
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Aucun membre n'est actuellement disponible dans votre région
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-orange-600" />
          Membres en déplacement
          <span className="ml-auto text-sm font-normal text-muted-foreground">
            {members.length}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {members.map((item) => (
          <div
            key={item.user_id}
            className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
          >
            <div className="relative">
              <Avatar className="h-12 w-12">
                <AvatarImage src={item.member.image || undefined} />
                <AvatarFallback>
                  {item.member.name.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {/* Badge rouge pulsant */}
              <div className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse border-2 border-background" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-sm truncate">
                    {item.member.name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {item.member.company}
                  </p>
                </div>
              </div>

              <div className="mt-2 space-y-1">
                <div className="flex items-center gap-1.5 text-xs">
                  <MapPin className="h-3 w-3 text-orange-600" />
                  <span className="font-medium text-orange-600">
                    {item.current_location}
                  </span>
                </div>

                {item.location_message && (
                  <p className="text-xs text-muted-foreground italic">
                    "{item.location_message}"
                  </p>
                )}

                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  <span>
                    Jusqu'au {new Date(item.available_until).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              </div>

              <Button
                size="sm"
                variant="outline"
                className="mt-2 w-full"
                onClick={() => handleContact(item.member.id)}
              >
                <MessageCircle className="h-3 w-3 mr-1" />
                Contacter
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
